import { Padding, Util } from '@antv/g6';
import { Position } from '../../constants';
import { DrawOpts, Group, Item, NodeModel, Shape, ShapeAttrs } from '../../types';
import { ShapeType } from '../constants';

export const getClassNameFromShape = (shape: Shape): string =>
  shape?.get?.('className') || shape?.cfg?.className;

export const getNodeTitle = (model?: NodeModel, options?: Record<string, any>): string =>
  model?.title || model?.nodeData?.title || model?.defaultTitle || options?.defaultTitle || '';

export const getAbbreviatedNumber = (num = 0): string => {
  const ONE_MILLION = 1000000;
  const TEN_MILLION = ONE_MILLION * 10;
  const ONE_THOUSAND = 1000;
  const TEN_THOUSAND = ONE_THOUSAND * 10;

  if (num >= TEN_MILLION) return `${Math.round(num / ONE_MILLION)}m`;
  if (num >= ONE_MILLION) return `${(num / ONE_MILLION).toFixed(1)}m`;
  if (num >= TEN_THOUSAND) return `${Math.round(num / ONE_THOUSAND)}k`;
  if (num >= ONE_THOUSAND) return `${(num / ONE_THOUSAND).toFixed(1)}k`;
  return `${num}`;
};

export const isGroup = (item: Group | Shape): item is Group => item.isGroup();

export const getChild = (group: DrawOpts['group'], id = '') => {
  const children = group.get('children');
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.get('id') === id || child.get('name') === id || child.get('className') === id) {
      return child;
    }
  }
  return null;
};

/**
 * Gets the item in which a group is nested
 */
export const getItemFromGroup = (group: Group | Shape) => {
  // Establishing a max for performance sake and to avoid endless loops
  const MAX_DEPTH = 5;
  let depth = 0;
  let item = group?.get('item') as Item;

  if (item) return item;

  let parent = group?.getParent();

  while (parent && !item && depth < MAX_DEPTH) {
    depth += 1;
    item = parent?.get('item') as Item;
    if (item) break;
    const nextParent = parent?.getParent();
    // If there is no next parent, we've gone too far.
    if (!nextParent) break;
    parent = nextParent;
  }

  return item;
};

/**
 * Take any group within a item (IE: node/combo/group) and returns the first
 * group within that item.
 */
export const getRootGroup = (group: Group | Shape) => {
  const item = getItemFromGroup(group);
  const itemGroup = item?.getContainer();
  if (itemGroup) return itemGroup;
  return group.isGroup() ? (group as Group) : (group.getParent() as Group);
};

/**
 * Gets the sum of all the offsets given
 */
export const getOffsetsSum = (...offsets: Padding[]) =>
  offsets.reduce(
    (acc: number[], cur) => {
      const [accTop, accRight, accBottom, accLeft] = acc;
      const [top, right, bottom, left] = Util.formatPadding(cur ?? 0);
      return [accTop + top, accRight + right, accBottom + bottom, accLeft + left];
    },
    [0, 0, 0, 0],
  );

/**
 * Gets all offsets for a shape, given the following attrs:
 * - margin
 * - padding
 * - offset
 * These attrs can either be a number or an array of up to 4 numbers (top, right, bottom, left)
 */
export const getAllOffsets = (style?: ShapeAttrs) =>
  style ? getOffsetsSum(style.offset, style.margin, style.padding) : [0, 0, 0, 0];

const getRelevantPosition = (shape: Shape, position: Position = Position.Bottom) => {
  const bbox = shape?.getBBox?.() || {};
  switch (position) {
    case Position.Left:
      return bbox?.minX;
    case Position.Right:
      return bbox?.maxX;
    case Position.Top:
      return bbox?.minY;
    default:
      return bbox?.maxY;
  }
};

/**
 * Gets the position of a shape relative to any other shape
 */
export const getPositionRelativeToShape = ({
  relativeTo,
  style,
  defaultPosition,
  group,
  position = Position.Bottom,
}: {
  /** The shape that you're looking for this to be relative to */
  relativeTo: Shape | string;
  /** Style of the new shape */
  style?: ShapeAttrs;
  /** Position if the shape cannot be found */
  defaultPosition?: number;
  /** Group of the node */
  group: DrawOpts['group'];
  /** Position relative to the shape */
  position?: Position;
}) => {
  const shape = typeof relativeTo === 'string' ? group.findByClassName(relativeTo) : relativeTo;
  const [top, right, bottom, left] = getAllOffsets(style);
  const pos = getRelevantPosition(shape, position) ?? defaultPosition ?? 0;
  switch (position) {
    case Position.Left:
      return pos - right;
    case Position.Right:
      return pos + left;
    case Position.Top:
      return pos - bottom;
    default:
      return pos + top;
  }
};

const isNumber = (num?: number): num is number => typeof num === 'number' && !Number.isNaN(num);

/** Adjusts the starting point of the attrs */
export const adjustAttrsPosition = (
  attrs: ShapeAttrs,
  { x, y }: { x?: number; y?: number } = {},
) => ({
  ...attrs,
  x: isNumber(x) ? (attrs.x ?? 0) + x : attrs.x,
  y: isNumber(y) ? (attrs.y ?? 0) + y : attrs.y,
});

/**
 * Removes a shape from the canvas and destroys it
 */
export const removeShape = (shape?: Shape | Group | null) => shape?.remove(true);

/** Get the shape linked to another shape */
export const getLinkedShape = (
  shape: Shape,
  linkName: ShapeType | string,
): Shape | Group | null | undefined => shape.get(linkName);

/**
 * Destroy the linked shape if it exists, and unset the reference on the shape.
 */
export const removeLinkedShape = (shape: Shape, linkName: ShapeType | string) => {
  removeShape(getLinkedShape(shape, linkName));
  shape.set(linkName, null);
};

/**
 * Set a shape as linked to another shape.  Destroy any shapes with this shape
 * type that were previously linked to this shape.
 */
export const addLinkedShape = (shape: Shape, linkName: ShapeType | string, linkedShape: Shape) => {
  // Remove any previously linked shapes of this type
  removeLinkedShape(shape, linkName);
  shape.set(linkName, linkedShape);
};
